<template>
  <v-container fluid class="pa-0">
    <UserCards
      :users="turnosStore.users || []"
      :selected-user-id="selectedUser?.id"
      :schedules="schedules || {}"
      :days="visibleDays"
      @user-selected="handleUserClick"
      @show-vacaciones="handleShowVacaciones"
    />

    <CalendarNavigation
      v-model:week-offset="weekOffset"
      :selected-year="selectedYear"
      :turnosChanged="turnosChanged"
      :selected-user-id="selectedUser?.id"
      @export="saveTurnos"
      @yearChange="handleYearChange"
      @weekChanged="handleWeekChange"
      @previsionModeChanged="handlePrevisionModeChanged"
    />

    <ViewAndShiftsTable
      v-model:week-offset="weekOffset"
      :selected-year="selectedYear"
      :time-slots="turnosStore.timeSlots"
      :schedules="schedules"
      :users="turnosStore.users"
      :puestoId="props.puestoId"
      @slot-click="handleSlotClick"
      @chip-click="handleChipClick"
    />

    <!-- Nuevo componente modal -->
    <VacacionesModal
      v-model="showVacacionesModal"
      :vacaciones="vacacionesPorMes"
      :year="selectedYear"
      :selected-user="selectedUser"
    />

    <ConfirmationDialog
      title="Hay cambios sin guardar"
      content="Si continúas, se perderán los cambios."
      v-model="showModal"
      :puestoId="props.puestoId"
      @confirm="onModalConfirm"
      @cancel="onModalCancel"
    />
  </v-container>
</template>

<script setup>
console.log('Component setup initialized')
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import UserCards from './subcomponents/UserCards.vue'
import CalendarNavigation from './subcomponents/CalendarNavigation.vue'
import ViewAndShiftsTable from './subcomponents/ViewAndShiftsTable.vue'
// import ShiftsTable from './subcomponents/ShiftsTable.vue'
import { useTurnosStore } from '@/stores/turnosStore'
import { getISOWeekNumber, getTodayDate } from '@/utils/helpers'
import ConfirmationDialog from '../common/ConfirmationDialog.vue'
import { useRouter } from 'vue-router'
import VacacionesModal from './subcomponents/VacacionesModal.vue'

// import { useDialog } from 'path/to/dialog/composable'
// Estado reactivo
const selectedUser = ref(null)
const schedules = ref({})
const weekOffset = ref(0)
const turnosStore = useTurnosStore()
const selectedYear = ref(getTodayDate().getFullYear())
const showModal = ref(false)
const showVacacionesModal = ref(false)
const vacacionesPorMes = ref(Array(12).fill(0))
// controlamos si hubo cambios en los turnos
const turnosChanged = ref(false)
// Estado para el modo de previsión
const previsionMode = ref(false)

const router = useRouter()
const previousRoute = ref(null)

const props = defineProps({
  puestoId: {
    type: String,
    required: false
  }
})

// Computed properties
const visibleDays = computed(() => {
  const firstDay = getMondayOfWeek(weekOffset.value || 1, selectedYear.value)
  const days = []

  for (let i = 0; i < 28; i++) {
    const day = new Date(firstDay)
    day.setDate(firstDay.getDate() + i)
    days.push(day)
  }

  return days
})

// Métodos

onMounted(async () => {
  window.dispatchEvent(new CustomEvent('toggle-drawer'))
  turnosStore.setTimeSlotsPuesto(props.puestoId)
  const today = getTodayDate()

  console.log(
    '🧪 ManageShifts onMounted - Fecha simulada:',
    today.toLocaleDateString('es-ES')
  )

  // Calcular el año ISO de la semana (igual que executeGoToToday)
  const tempDate = new Date(today)
  tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7))
  selectedYear.value = tempDate.getFullYear()
  weekOffset.value = Math.max(1, getISOWeekNumber(today))

  console.log(
    '📊 Año inicial:',
    selectedYear.value,
    '| Semana inicial:',
    weekOffset.value
  )

  // Guardamos la ruta inicial
  previousRoute.value = router.currentRoute.value.path
  setTimeout(async () => {
    console.log('initializeData desde onMounted')
    await initializeData()
  }, 500)
})

onUnmounted(() => {
  console.log('Component unmounted')
})

const getDateKey = (date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

// Event handlers
const handleUserClick = (user) => {
  selectedUser.value = selectedUser.value?.id === user.id ? null : user
}

const removeUserFromDay = (schedules, dayStr, userId) => {
  // Remover al usuario de cualquier posición en ese día
  Object.keys(schedules[dayStr] || {}).forEach((slotId) => {
    if (schedules[dayStr][slotId]) {
      schedules[dayStr][slotId] = schedules[dayStr][slotId].filter(
        (u) => u.id !== userId
      )
    }
  })
}

const handleChipClick = ({ day, slotId, user }) => {
  console.log('handleChipClick', day, slotId, user)
  const multiUserSlots = ['fiesta', 'vac', 'sob', 'baja', 'papelera']
  const isMultiUserSlot = multiUserSlots.includes(slotId)

  // Si no hay usuario seleccionado, no hacemos nada
  if (!selectedUser.value) return

  const dayStr = getDateKey(day)
  const newSchedules = { ...schedules.value }

  // Inicializar estructuras si no existen
  if (!newSchedules[dayStr]) {
    newSchedules[dayStr] = {}
  }
  if (!newSchedules[dayStr]['sob']) {
    newSchedules[dayStr]['sob'] = []
  }
  if (!newSchedules[dayStr][slotId]) {
    newSchedules[dayStr][slotId] = []
  }

  // Si el usuario clickeado es el mismo que el seleccionado y estamos en modo previsión,
  // simplemente actualizamos la propiedad prevision sin mover el chip
  if (user.id === selectedUser.value.id && previsionMode.value) {
    // Encontrar el índice del usuario en el array
    const userIndex = newSchedules[dayStr][slotId].findIndex(
      (u) => u.id === user.id
    )
    if (userIndex !== -1) {
      // Actualizar la propiedad prevision
      newSchedules[dayStr][slotId][userIndex] = {
        ...newSchedules[dayStr][slotId][userIndex],
        prevision: !newSchedules[dayStr][slotId][userIndex].prevision
      }
      turnosChanged.value = true
      schedules.value = newSchedules
      return
    }
  }

  // Crear una copia del usuario seleccionado con la propiedad prevision
  const selectedUserWithPrevision = {
    ...selectedUser.value,
    prevision: previsionMode.value // Usar el estado actual del modo previsión
  }

  // Mantener la propiedad prevision del usuario clickeado
  const userWithPrevision = {
    ...user,
    prevision: user.prevision || false
  }

  if (isMultiUserSlot) {
    if (user.id === selectedUser.value.id) {
      // Si el chip clickeado es del usuario seleccionado, lo removemos
      newSchedules[dayStr][slotId] = newSchedules[dayStr][slotId].filter(
        (u) => u.id !== user.id
      )
      // Y lo movemos a 'sob'
      if (!newSchedules[dayStr]['sob'].find((u) => u.id === user.id)) {
        newSchedules[dayStr]['sob'].push(userWithPrevision)
      }
    } else {
      // Si el chip es de otro usuario y tenemos un usuario seleccionado,
      // añadimos el usuario seleccionado a este slot

      // Primero removemos al usuario seleccionado de cualquier otro slot de ese día
      Object.keys(newSchedules[dayStr]).forEach((otherSlotId) => {
        if (Array.isArray(newSchedules[dayStr][otherSlotId])) {
          newSchedules[dayStr][otherSlotId] = newSchedules[dayStr][
            otherSlotId
          ].filter((u) => u.id !== selectedUser.value.id)
        }
      })

      // Luego lo añadimos al slot actual si no está ya
      if (
        !newSchedules[dayStr][slotId].find(
          (u) => u.id === selectedUser.value.id
        )
      ) {
        newSchedules[dayStr][slotId].push(selectedUserWithPrevision)
      }
    }
  } else {
    // Para turnos normales, mantenemos la lógica de un solo usuario
    if (user.id === selectedUser.value.id) {
      // Si el chip clickeado es del usuario seleccionado, lo removemos
      removeUserFromDay(newSchedules, dayStr, user.id)
      newSchedules[dayStr]['sob'].push(userWithPrevision)
    } else {
      // Si el chip es de otro usuario, lo movemos a 'sob' y ponemos al usuario seleccionado
      removeUserFromDay(newSchedules, dayStr, user.id)
      newSchedules[dayStr]['sob'].push(userWithPrevision)
      removeUserFromDay(newSchedules, dayStr, selectedUser.value.id)
      newSchedules[dayStr][slotId] = [selectedUserWithPrevision]
    }
  }

  turnosChanged.value = true
  schedules.value = newSchedules
}

const updateSchedule = (day, slotId) => {
  if (!selectedUser.value || slotId === 'sob') return
  turnosChanged.value = true
  const dayStr = getDateKey(day)
  const newSchedules = { ...schedules.value }
  console.log('newSchedules', dayStr, day.toISOString())

  // Lista de turnos que permiten múltiples usuarios
  const multiUserSlots = ['fiesta', 'vac', 'sob', 'baja', 'papelera']
  const isMultiUserSlot = multiUserSlots.includes(slotId)

  // Inicializar el día si no existe
  if (!newSchedules[dayStr]) {
    newSchedules[dayStr] = {}
  }

  // Inicializar el slot si no existe
  if (!newSchedules[dayStr][slotId]) {
    newSchedules[dayStr][slotId] = []
  }

  // Crear una copia del usuario seleccionado con la propiedad prevision
  const userWithPrevision = {
    ...selectedUser.value,
    prevision: previsionMode.value // Usar el estado actual del modo previsión
  }

  if (isMultiUserSlot) {
    // Para slots multi-usuario
    // Verificar si el usuario ya está en este slot
    const userInSlot = newSchedules[dayStr][slotId].some(
      (u) => u.id === selectedUser.value.id
    )

    if (userInSlot) {
      // Si el usuario ya está en el slot, lo removemos
      newSchedules[dayStr][slotId] = newSchedules[dayStr][slotId].filter(
        (u) => u.id !== selectedUser.value.id
      )
      // Y lo movemos a 'sob'
      if (!newSchedules[dayStr]['sob']) {
        newSchedules[dayStr]['sob'] = []
      }
      if (
        !newSchedules[dayStr]['sob'].find((u) => u.id === selectedUser.value.id)
      ) {
        newSchedules[dayStr]['sob'].push(userWithPrevision)
      }
    } else {
      // Si el usuario no está en el slot, primero lo removemos de cualquier otro slot
      Object.keys(newSchedules[dayStr]).forEach((otherSlotId) => {
        if (Array.isArray(newSchedules[dayStr][otherSlotId])) {
          newSchedules[dayStr][otherSlotId] = newSchedules[dayStr][
            otherSlotId
          ].filter((u) => u.id !== selectedUser.value.id)
        }
      })
      // Y lo añadimos al slot seleccionado, manteniendo los usuarios existentes
      newSchedules[dayStr][slotId].push(userWithPrevision)
    }
  } else {
    // Para slots normales (un solo usuario)
    // Remover al usuario seleccionado de cualquier posición en ese día
    Object.keys(newSchedules[dayStr]).forEach((otherSlotId) => {
      if (Array.isArray(newSchedules[dayStr][otherSlotId])) {
        newSchedules[dayStr][otherSlotId] = newSchedules[dayStr][
          otherSlotId
        ].filter((u) => u.id !== selectedUser.value.id)
      }
    })

    // Colocar el usuario seleccionado en el slot
    newSchedules[dayStr][slotId] = [userWithPrevision]
  }

  schedules.value = newSchedules
}

const handleSlotClick = async ({ day, slotId }) => {
  console.log('handleSlotClick', day, slotId)
  if (!selectedUser.value) return
  updateSchedule(day, slotId)
}

const saveTurnos = async () => {
  const formattedSchedules = []
  Object.entries(schedules.value).forEach(([date, daySchedules]) => {
    Object.entries(daySchedules).forEach(([slotId, users]) => {
      users.forEach((user) => {
        formattedSchedules.push({
          fecha: date,
          usuario_id: user.id,
          turno: slotId,
          prevision: user.prevision || false
        })
      })
    })
  })

  try {
    // console.log('formattedSchedules', formattedSchedules)
    await turnosStore.saveTurnos(formattedSchedules)
    turnosChanged.value = false
    // Opcional: Mostrar mensaje de éxito
  } catch (error) {
    console.error('Error saving schedules:', error)
    // Opcional: Mostrar mensaje de error
  }
}

function getMondayOfWeek(weekNumber, year) {
  // Asegurarnos de que weekNumber sea válido
  const validWeekNumber = Math.max(1, Math.min(53, weekNumber || 1))

  const firstDayOfYear = new Date(year, 0, 1)
  const firstDayOfWeek = firstDayOfYear.getDay()
  const daysToFirstThursday = (11 - firstDayOfWeek) % 7
  const firstThursday = new Date(year, 0, 1 + daysToFirstThursday)
  const firstMonday = new Date(firstThursday)
  firstMonday.setDate(firstThursday.getDate() - 3)
  const targetMonday = new Date(firstMonday)
  targetMonday.setDate(firstMonday.getDate() + (validWeekNumber - 1) * 7)

  return targetMonday
}

const initializeData = async () => {
  try {
    selectedUser.value = null

    currentLoadedPosition.value = props.puestoId
    const firstDay = getMondayOfWeek(weekOffset.value, selectedYear.value)
    const fecha = getDateKey(firstDay)
    schedules.value = await turnosStore.fetchTurnos(props.puestoId, fecha)
  } catch (error) {
    console.error('Error initializing data:', error)
  }
}

const onModalConfirm = async () => {
  showModal.value = false
  // if (pendingOperation) {
  turnosChanged.value = false
  console.log('Cambió puestoId de', props.puestoId)
  await initializeData()
  // pendingOperation = null
  // }
}

const onModalCancel = () => {
  showModal.value = false
  // pendingOperation = null
  // Volvemos a la ruta anterior
  if (previousRoute.value) {
    router.push(previousRoute.value)
  }
}

const currentLoadedPosition = ref(null)

const handleYearChange = async (year) => {
  selectedYear.value = year

  const today = getTodayDate()

  // Calcular el año ISO de la fecha de prueba
  const tempDate = new Date(today)
  tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7))
  const currentISOYear = tempDate.getFullYear()

  // Si el año es el actual (año ISO), mantener la semana actual
  // Si no, empezar desde la semana 1
  if (year === currentISOYear) {
    weekOffset.value = getISOWeekNumber(today)
    console.log(
      '📊 handleYearChange - Cambio a año actual ISO:',
      year,
      '| Semana:',
      weekOffset.value
    )
  } else {
    weekOffset.value = 1 // Resetear a la primera semana del año
    console.log('📊 handleYearChange - Cambio a año:', year, '| Semana: 1')
  }

  await initializeData()
}

const handleWeekChange = async (newWeek) => {
  weekOffset.value = newWeek
  await initializeData()
}

const handleShowVacaciones = async () => {
  if (!selectedUser.value) return

  try {
    const response = await turnosStore.getVacacionesPorMes(
      selectedUser.value.id,
      selectedYear.value
    )
    vacacionesPorMes.value = response.vacaciones_por_mes
    showVacacionesModal.value = true
  } catch (error) {
    console.error('Error al obtener vacaciones:', error)
  }
}

const handlePrevisionModeChanged = (mode) => {
  previsionMode.value = mode
  console.log('Modo previsión:', mode)
}

// Añadimos el watcher para puestoId
watch(
  () => props.puestoId,
  async (newValue, oldValue) => {
    turnosStore.setTimeSlotsPuesto(newValue)

    if (newValue !== oldValue && newValue !== currentLoadedPosition.value) {
      previousRoute.value = oldValue
        ? `/turnos/asignar-turnos/${oldValue}`
        : '/turnos/asignar-turnos'
      if (turnosChanged.value) {
        showModal.value = true
        return
      }
      console.log('Cambió puestoId de', oldValue, 'a', newValue)

      await initializeData()
    }
  }
)
</script>
